import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { BoardCard, BoardColumn, PhaseMoveResult } from "@/lib/types";

/**
 * Confirms an operator's manual phase move and captures why they made it.
 *
 * <p>The note is the reason this dialog exists rather than the drop applying
 * straight away. Email history only contains what was emailed, so a deal moved
 * forward by a phone call reads to the agent as silence. This is the one place
 * a human can say what actually happened — and what they write is given to the
 * agent as context when it composes the next follow-up.
 *
 * <p>Reached two ways, both landing here: dropping a card on a column, or the
 * card's Move action (which is what makes the feature usable by keyboard).
 */
export default function PhaseMoveDialog({
  card,
  fromColumn,
  columns,
  initialToPhaseId,
  strategySlug,
  onClose,
}: {
  card: BoardCard;
  fromColumn: BoardColumn;
  /** Every column that can be a target — phases only, in journey order. */
  columns: BoardColumn[];
  initialToPhaseId: string | null;
  strategySlug: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [toPhaseId, setToPhaseId] = useState<string>(
    initialToPhaseId ?? columns.find((c) => c.phaseId !== fromColumn.phaseId)?.phaseId ?? "",
  );
  const [note, setNote] = useState("");

  const target = columns.find((c) => c.phaseId === toPhaseId) ?? null;
  const name = card.fullName ?? card.email;

  const submit = useMutation<PhaseMoveResult>({
    mutationFn: () =>
      api.post<PhaseMoveResult>(`/api/v1/prospects/${card.id}/phase`, {
        toPhaseId,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", strategySlug] });
      queryClient.invalidateQueries({ queryKey: ["prospect", card.id] });
      queryClient.invalidateQueries({ queryKey: ["prospect-transitions", card.id] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Move prospect to another phase"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-lg">
        <h2 className="text-base font-semibold">Move {name}</h2>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          Currently in{" "}
          <span className="font-medium">{fromColumn.phaseName ?? "Not started"}</span>. Moving a
          prospect by hand is recorded in their timeline with whoever made the move.
        </p>

        <label className="mt-4 block text-sm font-medium" htmlFor="phase-move-target">
          Move to
        </label>
        <select
          id="phase-move-target"
          value={toPhaseId}
          onChange={(e) => setToPhaseId(e.target.value)}
          className="mt-1 w-full rounded-md border border-[var(--border)] bg-white px-3 py-1.5 text-sm"
        >
          {columns.map((c) => (
            <option key={c.phaseId} value={c.phaseId ?? ""} disabled={c.phaseId === fromColumn.phaseId}>
              {c.phaseName ?? c.phaseSlug}
              {c.phaseId === fromColumn.phaseId ? " (current phase)" : ""}
            </option>
          ))}
        </select>

        <label className="mt-4 block text-sm font-medium" htmlFor="phase-move-note">
          Why are you moving them?{" "}
          <span className="font-normal text-[var(--fg-muted)]">(optional)</span>
        </label>
        <textarea
          id="phase-move-note"
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={2000}
          placeholder="e.g. Spoke on the phone today — they agreed to send the paperwork next week and asked us not to chase before then."
          className="mt-1 w-full rounded-md border border-[var(--border)] p-2 text-sm"
        />
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          The agent reads this when it writes the next follow-up, so anything that happened off
          email — a call, a meeting, what was agreed — belongs here.
        </p>

        {target?.terminal ? (
          <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
            <span className="font-medium">{target.phaseName}</span> ends the journey. The agent
            stops working this prospect once they are there.
          </div>
        ) : null}

        {submit.error ? (
          <div className="mt-3 text-sm text-[var(--danger)]">
            {submit.error instanceof ApiError
              ? submit.error.status === 409
                ? "They are already in that phase — the board may be out of date."
                : `Could not move this prospect (HTTP ${submit.error.status}).`
              : "Could not move this prospect."}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={submit.isPending || !toPhaseId || toPhaseId === fromColumn.phaseId}
            className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {submit.isPending ? "Moving…" : "Move prospect"}
          </button>
        </div>
      </div>
    </div>
  );
}
